class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    /* Delegate: #CartDrawer innerHTML is replaced after cart updates (replaceCartDrawerRootHTML),
       which removes a directly-bound overlay node — host-level delegation stays valid. */
    this._onOverlayClick = (evt) => {
      const overlay = evt.target && evt.target.closest && evt.target.closest('#CartDrawer-Overlay');
      if (!overlay || !this.contains(overlay)) return;
      this.close();
    };
    this.addEventListener('click', this._onOverlayClick);
    this.setHeaderCartIconAccessibility();
    this.normalizeState();
    /* `pageshow` fires in two cases:
         1. After the initial page load (event.persisted === false)
         2. When the page is restored from the bfcache (persisted === true)
       Only case 2 needs a state reset. Case 1 was racing against
       early-click flows: a user who clicked the cart icon before the
       `load` event would see the drawer open from openFromCartIcon ->
       fetchMissingSections -> renderContents -> open() (the active
       class is added inside a setTimeout, so it lands a few ms after
       the click), and then pageshow would fire shortly after `load`
       and call normalizeState(), which removes `active` and closes
       the drawer immediately. Symptom: "click cart -> drawer opens,
       mutates, closes; click again to actually use it."

       Gating on `persisted` keeps the bfcache reset correct (the
       drawer can be left in active state when navigating away, and
       must reset on return) without killing freshly-opened drawers
       during slow initial loads. */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.normalizeState();
    });
  }

  normalizeState() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    }

    if (this._cartIconDelegatedBound) return;
    this._cartIconDelegatedBound = true;

    this._onCartIconClick = (event) => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.openFromCartIcon(link);
    };

    this._onCartIconKeydown = (event) => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link) return;
      const code = (event.code || '').toUpperCase();
      if (code === 'SPACE' || code === 'ENTER') {
        event.preventDefault();
        this.openFromCartIcon(link);
      }
    };

    document.addEventListener('click', this._onCartIconClick, true);
    document.addEventListener('keydown', this._onCartIconKeydown);
  }

  _forceScrollToTop() {
    const host = this.querySelector('[data-dd-cart-items]');
    if (host) host.scrollTop = 0;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const loginDrawer = document.querySelector('login-drawer.active');
    if (loginDrawer) {
      loginDrawer.classList.remove('active');
      document.body.classList.remove('login-drawer-open');
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const needsScrollReset = !!this._scrollToTopOnOpen;
    this._scrollToTopOnOpen = false;

    if (needsScrollReset) this._forceScrollToTop();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    document.dispatchEvent(new CustomEvent('drawer:opened'));

    /* Wait for the drawer host's OWN slide-in transition to end before
       focus-trapping. Bug history: the prior implementation used
       `{ once: true }` with no event-target filter, so it caught the
       FIRST `transitionend` that bubbled up from anywhere — including
       descendant transitions that fire much later (e.g. the ktip
       cross-sell dropdown's chevron rotate at 180ms or its menu's
       opacity at 120ms after open). When that happened the handler
       called `trapFocus` → `.drawer__inner.focus()` → iOS Safari
       scrolled `.dd-cart-items` so the focused container was at the
       top of view, yanking the cart drawer's scrollTop to 0 mid-tap.
       Symptom: tap any in-cart control after scrolling → cart snaps
       to top under your finger. Filter on `event.target === this` so
       only the host's own transitionend fires this handler, and use
       a self-removing listener (instead of `{ once: true }`) so
       descendant transitionend events don't consume the slot. */
    const handleHostTransitionEnd = (event) => {
      if (event.target !== this) return;
      this.removeEventListener('transitionend', handleHostTransitionEnd);
      if (needsScrollReset) this._forceScrollToTop();
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      if (!containerToTrapFocusOn) return;
      const focusElement =
        this.querySelector('.drawer__inner') || this.querySelector('.drawer__close') || containerToTrapFocusOn;
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', handleHostTransitionEnd);

    document.body.classList.add('overflow-hidden');
  }

  openFromCartIcon(triggeredBy) {
    this.setActiveElement(triggeredBy);
    const helpers = window.ddCartHelpers;
    if (helpers && typeof helpers.isSectionFooterFresh === 'function' && helpers.isSectionFooterFresh()) {
      this.open(triggeredBy);
      return;
    }
    this.fetchMissingSections()
      .then((sections) => {
        if (sections && sections['cart-drawer']) {
          this.renderContents({ sections });
          return;
        }
        this.open(triggeredBy);
      })
      .catch(() => {
        this.open(triggeredBy);
      });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const summaryBody = cartDrawerNote.nextElementSibling;
    if (summaryBody && summaryBody.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', summaryBody.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (
      parsedState &&
      Array.isArray(parsedState.items) &&
      typeof parsedState.item_count === 'number' &&
      window.ddCartHelpers &&
      typeof window.ddCartHelpers.seedLatestCart === 'function'
    ) {
      window.ddCartHelpers.seedLatestCart(parsedState);
    }
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this._scrollToTopOnOpen = true;
    const renderWithSections = (sections, allowDrawerFallback = true) => {
      const payloadSections = sections || {};
      let didRenderDrawer = false;
      this.getSectionsToRender().forEach((section) => {
        const sectionHtml = payloadSections[section.id];
        if (!sectionHtml) return;

        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        if (
          section.selector === '#CartDrawer' &&
          window.ddCartHelpers &&
          typeof window.ddCartHelpers.patchDrawerFromSection === 'function'
        ) {
          const didPatch = window.ddCartHelpers.patchDrawerFromSection(sectionHtml);
          if (didPatch) {
            didRenderDrawer = true;
            return;
          }
        }

        const nextHTML = this.getSectionInnerHTML(sectionHtml, section.selector, section.id);
        if (!nextHTML) return;
        if (
          section.selector === '#CartDrawer' &&
          window.ddCartHelpers &&
          typeof window.ddCartHelpers.replaceCartDrawerRootHTML === 'function'
        ) {
          window.ddCartHelpers.replaceCartDrawerRootHTML(sectionElement, nextHTML);
          didRenderDrawer = true;
        } else {
          sectionElement.innerHTML = nextHTML;
          if (section.selector === '#CartDrawer') didRenderDrawer = true;
        }
      });

      if (allowDrawerFallback && !didRenderDrawer) {
        this.fetchMissingSections()
          .then((fallbackSections) => {
            renderWithSections(fallbackSections || payloadSections, false);
          })
          .catch((err) => {
            console.warn('[cart-drawer] drawer section parse fallback failed', err);
            renderWithSections(payloadSections, false);
          });
        return;
      }

      setTimeout(() => {
        this.setHeaderCartIconAccessibility();
        this._forceScrollToTop();
        this.open();
      });
    };

    const sections = parsedState && parsedState.sections ? parsedState.sections : null;
    const hasDrawerSection = sections && sections['cart-drawer'];
    if (hasDrawerSection) {
      renderWithSections(sections);
      return;
    }

    /* Defensive fallback: some add payloads occasionally arrive without rendered sections. */
    this.fetchMissingSections()
      .then((fallbackSections) => {
        renderWithSections(fallbackSections || sections || {});
      })
      .catch((err) => {
        console.warn('[cart-drawer] missing sections fallback failed', err);
        renderWithSections(sections || {});
      });
  }

  fetchMissingSections() {
    return fetch('/cart?sections=' + encodeURIComponent('cart-drawer,cart-icon-bubble'), {
      credentials: 'same-origin',
      cache: 'no-store'
    }).then((response) => {
      if (!response.ok) throw new Error('Missing sections fetch failed (' + response.status + ')');
      return response.json();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section', fallbackId = null) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let target = selector ? doc.querySelector(selector) : null;
    if (!target && fallbackId) target = doc.getElementById(fallbackId);
    if (!target && selector === '.shopify-section') target = doc.querySelector('[id^="shopify-section-"]');
    if (!target && selector === '#CartDrawer') target = doc.querySelector('cart-drawer #CartDrawer');
    if (!target && selector === '#CartDrawer') return '';
    if (!target && selector === '.shopify-section') return '';
    if (!target) target = doc.body;
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  disconnectedCallback() {
    if (this._onCartIconClick) {
      document.removeEventListener('click', this._onCartIconClick, true);
      this._onCartIconClick = null;
    }
    if (this._onCartIconKeydown) {
      document.removeEventListener('keydown', this._onCartIconKeydown);
      this._onCartIconKeydown = null;
    }
    this._cartIconDelegatedBound = false;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
